import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import BasicModal from './BasicModal';
import LoadingSpinner from '../LoadingSpinner';
import PremiumIconCrown from '../PremiumIconCrown';
import ExportUpsellCard from '../ExportUpsellCard';
import { Listbox } from '../Listbox';
import { GET_PREMIUM_ACCESS_URL } from '../../utils/isPremiumUser';
import PremiumOfferCta from '../PremiumOfferCta';
import { usePremiumOffer } from '../../contexts/premiumOffer';
import { useExportGate } from '../../utils/exportTrialState';
import { Mixpanel } from '../../services/Mixpanel';
import { getCurriculumCourse, getCurriculumCourses } from '../../services/CurriculumService';
import {
  MAX_CURRICULUM_LESSONS,
  curriculumStandards,
  flattenUnits,
  getCurriculum,
  lessonLabel,
} from '../../utils/curriculum';

// bts-2026-freemium: same overlay treatment as the standards modal — the first
// rows read as a live teaser, the rest locks. See AddSkillsModal.
const GATE_FADE_MASK = 'linear-gradient(to bottom, transparent 0%, black 30%)';

/**
 * Align → Curriculum picker: choose a course from a curriculum's scope and
 * sequence, then the lesson(s) this resource sits alongside.
 *
 * Reads the files published to the static bucket by diffit-standards' `curriculum`
 * command — one index plus one file per course, each lesson carrying the standards
 * it teaches. Selection is returned to the caller, which sends those standards on
 * the create request like any other content standard.
 */
export default function CurriculumModal({
  showModal,
  setShowModal,
  onSubmit,
  curriculumId,
  initialSelection = null,
  allowCourseOnly = false,
}) {
  const initialFocusRef = useRef();
  // The caller mounts this only for a curriculum the published index lists.
  const curriculum = getCurriculum(curriculumId);

  const { isOffered } = usePremiumOffer();
  const { tier, gateActive, showTrialCountdown } = useExportGate();
  const curriculumGated = gateActive && tier === 'free';

  const [courses, setCourses] = useState([]);
  const [courseSlug, setCourseSlug] = useState(initialSelection?.courseSlug || null);
  const [course, setCourse] = useState(null);
  const [selectedLessons, setSelectedLessons] = useState(initialSelection?.lessons || []);
  const [expandedUnitIds, setExpandedUnitIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [isLoadingScope, setIsLoadingScope] = useState(false);
  const [error, setError] = useState(null);

  const selectedCourse = courses.find((c) => c.slug === courseSlug) || null;
  const courseName = selectedCourse?.name || initialSelection?.courseName || null;

  useEffect(() => {
    if (showModal) {
      Mixpanel.track('Open Align Curriculum Modal', {
        curriculum_id: curriculum.id,
        curriculum_source: curriculum.source,
      });
    }
  }, [showModal, curriculum.id, curriculum.source]);

  // Courses for the curriculum
  useEffect(() => {
    let cancelled = false;
    setIsLoadingCourses(true);
    setError(null);
    getCurriculumCourses(curriculum.id)
      .then((result) => !cancelled && setCourses(result))
      .catch(() => {
        if (cancelled) return;
        setError('Curriculum data is unavailable right now.');
        // The published files are the one dependency this picker has, and a bare
        // axios instance has no error interceptor — without this, a 403/404 on the
        // index is invisible to both Sentry and Mixpanel.
        Mixpanel.track('Align Curriculum Load Failed', {
          curriculum_id: curriculum.id,
          resource: 'courses.json',
        });
      })
      .finally(() => !cancelled && setIsLoadingCourses(false));
    return () => {
      cancelled = true;
    };
  }, [curriculum.id]);

  // The chosen course's published file (units, lessons, and their standards)
  useEffect(() => {
    if (!courseSlug) {
      setCourse(null);
      return undefined;
    }
    let cancelled = false;
    setIsLoadingScope(true);
    setError(null);
    getCurriculumCourse(curriculum.id, courseSlug)
      .then((result) => !cancelled && setCourse(result))
      .catch(() => {
        if (cancelled) return;
        setError('Could not load this course’s lessons.');
        Mixpanel.track('Align Curriculum Load Failed', {
          curriculum_id: curriculum.id,
          resource: courseSlug,
        });
      })
      .finally(() => !cancelled && setIsLoadingScope(false));
    return () => {
      cancelled = true;
    };
  }, [curriculum.id, courseSlug]);

  const units = useMemo(() => flattenUnits(course), [course]);

  const filteredUnits = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return units;
    return units
      .map((unit) => ({
        ...unit,
        lessons: unit.lessons.filter((lesson) =>
          `${unit.label} ${lessonLabel(lesson)}`.toLowerCase().includes(query)
        ),
      }))
      .filter((unit) => unit.lessons.length > 0);
  }, [units, searchQuery]);

  // A search narrows to a handful of lessons, so keep matching units open.
  const isUnitExpanded = useCallback(
    (unitId) => Boolean(searchQuery.trim()) || expandedUnitIds.includes(unitId),
    [searchQuery, expandedUnitIds]
  );

  const toggleUnit = (unitId) =>
    setExpandedUnitIds((prev) =>
      prev.includes(unitId) ? prev.filter((id) => id !== unitId) : [...prev, unitId]
    );

  const isSelected = (lessonId) => selectedLessons.some((l) => l.id === lessonId);
  const atCapacity = selectedLessons.length >= MAX_CURRICULUM_LESSONS;

  const toggleLesson = (lesson) => {
    setSelectedLessons((prev) => {
      if (prev.some((l) => l.id === lesson.id)) return prev.filter((l) => l.id !== lesson.id);
      if (prev.length >= MAX_CURRICULUM_LESSONS) return prev;
      return [...prev, lesson];
    });
  };

  // Lessons from a previous course would send a course/lesson mismatch. Headless UI
  // fires onChange even when the current option is re-picked, so bail out first —
  // otherwise opening the dropdown to check the course clears the lessons.
  const changeCourse = (nextSlug) => {
    if (nextSlug === courseSlug) return;
    setCourseSlug(nextSlug);
    setSelectedLessons([]);
    setExpandedUnitIds([]);
    setSearchQuery('');
  };

  const selectedStandardCodes = useMemo(
    () => curriculumStandards({ lessons: selectedLessons }).map((s) => s.id),
    [selectedLessons]
  );

  const submitForm = () => {
    Mixpanel.track('Submit Align Curriculum Modal', {
      curriculum_id: curriculum.id,
      curriculum_source: curriculum.source,
      course_slug: courseSlug,
      lessons_count: selectedLessons.length,
      standards_count: selectedStandardCodes.length,
    });
    onSubmit({
      curriculumId: curriculum.id,
      curriculumName: curriculum.name,
      curriculumSource: curriculum.source,
      // The course file may not have arrived yet on a reopened picker, and submit
      // is enabled from the first frame in that case — keep the id we were given.
      courseId: course?.course_id || initialSelection?.courseId || null,
      courseSlug,
      courseName,
      lessons: selectedLessons,
    });
    setShowModal(false);
  };

  const hasInitialSelection = Boolean(initialSelection?.lessons?.length);
  // Courses: a message can align to a curriculum course alone; lessons are optional there.
  const isSubmitDisabled = allowCourseOnly
    ? !courseSlug
    : !selectedLessons.length && !hasInitialSelection;

  // Curriculum credit (CC BY 4.0 for IM). Last row of the list, where mt-auto keeps it at the box's
  // bottom edge in the empty and error states. While gated it renders below the
  // overlay's wrapper instead, so the blur can never cover it however many lines
  // it wraps to on a narrow screen.
  const creditRow = (
    <p className='mt-auto pt-4 pb-1 text-xs text-gray-500'>
      {curriculum.attribution}
      {curriculum.license && (
        <>
          {' '}
          Licensed under{' '}
          <a
            href={curriculum.license.url}
            target='_blank'
            rel='noreferrer'
            className='whitespace-nowrap underline hover:text-gray-700'>
            {curriculum.license.name}
          </a>
          .
        </>
      )}
    </p>
  );

  return (
    <BasicModal
      showModal={showModal}
      setShowModal={setShowModal}
      initialFocusRef={initialFocusRef}
      dialogPadding={''}
      maxWidth={'sm:max-w-3xl'}
      iconBackground={'bg-none'}>
      <div className='pt-8 px-8 h-full'>
        <div className='flex flex-row items-baseline sm:max-w-3xl'>
          <h1 className='text-xl sm:text-2xl font-semibold text-gray-800 mr-2'>
            Align to a Curriculum
          </h1>
          {(!gateActive || tier !== 'paid') && (
            <PremiumIconCrown className='h-5 w-5' wrapperClassName='translate-y-[6px]' />
          )}
        </div>
        <span className='mt-1 text-gray-800 text-xs sm:text-sm hidden sm:block'>
          {allowCourseOnly
            ? `Choose the ${curriculum.displayName} course and any lessons (up to ${MAX_CURRICULUM_LESSONS}) to align this message with.`
            : `Choose the ${curriculum.displayName} lessons to align with (up to ${MAX_CURRICULUM_LESSONS}).`}
        </span>

        {showTrialCountdown && (
          <div className='mt-3'>
            <ExportUpsellCard
              mode='trial'
              location='align_curriculum_modal'
              trackEvent={(name, props) => Mixpanel.track(name, props)}
            />
          </div>
        )}

        <div className='mt-3 flex flex-col sm:flex-row gap-2 w-full'>
          <div className='w-full sm:flex-grow'>
            <Listbox
              selected={{
                name:
                  selectedCourse?.name ||
                  courseName ||
                  (isLoadingCourses ? 'Loading…' : 'Select a course'),
                value: courseSlug,
              }}
              setSelected={(selection) =>
                changeCourse(typeof selection === 'string' ? selection : selection?.value)
              }
              options={courses.map((c) => ({ name: c.name, value: c.slug }))}
              label={'Course'}
              placeholder={'Select a course'}
            />
          </div>
          <div className='w-full sm:flex-grow'>
            <label
              htmlFor='curriculum-lesson-search'
              className='block text-sm font-medium leading-6 text-gray-900'>
              Search lessons
            </label>
            <div className='relative mt-1'>
              <MagnifyingGlassIcon className='pointer-events-none absolute left-2 top-2 h-5 w-5 text-gray-400' />
              <input
                id='curriculum-lesson-search'
                type='text'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                disabled={!courseSlug}
                placeholder='Search by lesson title'
                className='block w-full rounded-md border-0 py-1.5 pl-9 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-emerald-500 sm:text-sm sm:leading-6 disabled:bg-gray-50'
              />
            </div>
          </div>
        </div>

        <div className='relative mt-3'>
          <div className='h-[22rem] overflow-y-auto scrollbar-dropdown pr-1'>
            <div className='flex min-h-full flex-col'>
              {error && (
                <div className='flex flex-1 items-center justify-center px-6 text-center text-gray-600'>
                  <div>
                    <div className='sm:text-xl font-semibold'>{error}</div>
                    <div className='text-xs sm:text-normal mt-1'>
                      Please try again in a few minutes.
                    </div>
                  </div>
                </div>
              )}
              {!error && (isLoadingCourses || isLoadingScope) && (
                <div className='flex flex-1 w-full justify-center'>
                  <LoadingSpinner size='6' className='self-center' />
                </div>
              )}
              {!error && !isLoadingCourses && !isLoadingScope && !courseSlug && (
                <div className='flex flex-1 items-center justify-center text-gray-600'>
                  <div className='sm:text-xl font-semibold'>Select a course to see its lessons</div>
                </div>
              )}
              {!error && !isLoadingScope && courseSlug && filteredUnits.length === 0 && (
                <div className='flex flex-1 items-center justify-center text-gray-600'>
                  <div className='sm:text-xl font-semibold'>
                    {searchQuery
                      ? 'No lessons match your search'
                      : 'This course has no lessons yet'}
                  </div>
                </div>
              )}
              {/* Wrapped so the last unit stays :last-child and drops its border. */}
              <div>
                {!error &&
                  !isLoadingScope &&
                  filteredUnits.map((unit) => (
                    <div key={unit.id} className='border-b border-gray-100 last:border-b-0'>
                      <button
                        type='button'
                        onClick={() => toggleUnit(unit.id)}
                        className='w-full flex items-center gap-2 px-2 py-2.5 text-left text-sm font-medium text-gray-800 hover:bg-gray-50 rounded-lg'>
                        {isUnitExpanded(unit.id) ? (
                          <ChevronDownIcon className='h-4 w-4 flex-shrink-0 text-gray-400' />
                        ) : (
                          <ChevronRightIcon className='h-4 w-4 flex-shrink-0 text-gray-400' />
                        )}
                        <span className='truncate'>{unit.label}</span>
                        <span className='ml-auto text-xs font-normal text-gray-500'>
                          {unit.lessons.length} lessons
                        </span>
                      </button>
                      {isUnitExpanded(unit.id) && (
                        <ul className='pb-2'>
                          {unit.lessons.map((lesson) => {
                            const selected = isSelected(lesson.id);
                            return (
                              <li key={lesson.id}>
                                <button
                                  type='button'
                                  onClick={() => toggleLesson(lesson)}
                                  disabled={!selected && atCapacity}
                                  className={`w-full flex items-start gap-2 pl-8 pr-3 py-2 text-left text-sm rounded-lg transition-colors ${
                                    selected
                                      ? 'bg-emerald-50 text-emerald-900'
                                      : 'text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:hover:bg-transparent disabled:cursor-not-allowed'
                                  }`}>
                                  <input
                                    type='checkbox'
                                    readOnly
                                    checked={selected}
                                    tabIndex={-1}
                                    className='mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500'
                                  />
                                  <span className='flex flex-col'>
                                    <span>{lessonLabel(lesson)}</span>
                                    {lesson.sectionName && (
                                      <span className='text-xs text-gray-500'>
                                        {lesson.sectionName}
                                      </span>
                                    )}
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  ))}
              </div>
              {!curriculumGated && creditRow}
            </div>
          </div>
          {curriculumGated && (
            <div
              style={{ maskImage: GATE_FADE_MASK, WebkitMaskImage: GATE_FADE_MASK }}
              className='absolute inset-x-0 bottom-0 h-3/5 flex flex-col justify-center bg-white backdrop-blur-[3px] pointer-events-none'>
              <div className='px-4 pointer-events-auto translate-y-6'>
                {isOffered ? (
                  // Not a button: the offer and its plans link are two targets, and nesting
                  // them inside one would make the click destination unpredictable.
                  <div className='mx-auto flex max-w-2xl items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-6 py-4 text-left shadow-lg'>
                    <PremiumOfferCta
                      variant='inline'
                      question='Need to align to your curriculum?'
                      location='align_curriculum_modal'
                    />
                  </div>
                ) : (
                  <button
                    type='button'
                    onClick={() => {
                      Mixpanel.track('Get Premium Access Clicked', {
                        location: 'align_curriculum_modal',
                      });
                      window.open(GET_PREMIUM_ACCESS_URL, '_blank');
                    }}
                    className='mx-auto flex max-w-2xl items-center gap-3 rounded-xl border border-gray-100 bg-gray-50 px-6 py-4 text-left shadow-lg hover:bg-gray-100'>
                    <PremiumIconCrown
                      className='h-6 w-6'
                      wrapperClassName='flex-shrink-0'
                      toolTipProps={{ text: undefined }}
                    />
                    <span className='flex flex-col text-xs text-gray-900'>
                      <span>Need to align to your curriculum?</span>
                      <span className='mt-0.5 inline-flex items-center font-medium text-amber-700'>
                        See Plans
                        <ChevronRightIcon className='ml-0.5 h-3 w-3' />
                      </span>
                    </span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        {curriculumGated && creditRow}
      </div>

      {!curriculumGated && (
        <div
          id='curriculum-modal-footer'
          className='bg-gray-100 p-2 sm:px-8 sm:py-3 w-full sticky bottom-0'>
          <div className='flex flex-col sm:flex-row gap-2 sm:items-center'>
            <div className='flex-1 min-w-0'>
              <div className='flex flex-row flex-wrap gap-2'>
                {selectedLessons.map((lesson) => (
                  <span
                    key={lesson.id}
                    className='inline-flex items-center gap-1 rounded-full bg-emerald-50 pl-3 pr-1 py-1 text-xs text-emerald-800 max-w-full'>
                    <span className='truncate'>{lessonLabel(lesson)}</span>
                    <button
                      type='button'
                      onClick={() => toggleLesson(lesson)}
                      className='rounded-full px-1.5 hover:bg-emerald-100'
                      aria-label={`Remove ${lessonLabel(lesson)}`}>
                      ✕
                    </button>
                  </span>
                ))}
              </div>
            </div>
            <button
              ref={initialFocusRef}
              type='button'
              onClick={submitForm}
              disabled={isSubmitDisabled}
              className={`self-end sm:self-center whitespace-nowrap inline-flex justify-center px-4 py-2 text-xs sm:text-sm font-medium border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 ${
                isSubmitDisabled
                  ? 'bg-gray-200 text-gray-900 cursor-not-allowed'
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}>
              Set Curriculum
            </button>
          </div>
        </div>
      )}
    </BasicModal>
  );
}
